import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Banner } from '../api/types';
import { rememberCatalogPosition } from '../lib/gameLaunch';

interface Props {
  cards: Banner[];
  loading?: boolean;
  /** Desktop aside grid vs mobile horizontal rail — class hook only. */
  layout?: 'auto' | 'rail' | 'aside';
}

type FallbackPromo = {
  id: string;
  slug: string;
  titleKey: string;
  tone: 'cashback' | 'vip' | 'crypto' | 'invite';
  link_url: string;
};

/** Order: VIP Cashback → Dost dəvəti → Kripto (used only when CMS campaign cards empty). */
const FALLBACK_PROMOS: FallbackPromo[] = [
  { id: 'fb-vip', slug: 'vip-cashback', titleKey: 'storyVip', tone: 'vip', link_url: '/vip' },
  { id: 'fb-invite', slug: 'dost-deveti', titleKey: 'storyInvite', tone: 'invite', link_url: '/referral' },
  { id: 'fb-crypto', slug: 'kripto-bonus', titleKey: 'storyCrypto', tone: 'crypto', link_url: '/cashier?tab=deposit' },
];

function resolveHref(card: Banner): string | null {
  if (card.action_type === 'none') return null;
  if (card.action_type === 'open_game' && card.game_public_id) {
    return `/play/${card.game_public_id}`;
  }
  if (card.action_type === 'campaign_info') {
    return `/campaigns/${card.slug}`;
  }
  if (card.link_url?.startsWith('/')) return card.link_url;
  return null;
}

export function CampaignCards({ cards, loading, layout = 'auto' }: Props) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const items = cards.filter((c) => (c.banner_type || 'campaign') === 'campaign');
  const useFallback = !loading && items.length === 0;
  const railClass = `campaign-rail${layout === 'aside' ? ' campaign-rail-aside' : ''}${layout === 'rail' ? ' campaign-rail-mobile' : ''}`;

  if (loading) {
    return (
      <div className={railClass} aria-hidden="true">
        {[0, 1, 2, 3].map((n) => (
          <div key={n} className="campaign-card skeleton" />
        ))}
      </div>
    );
  }

  if (useFallback) {
    return (
      <div className={railClass} role="list" aria-label={t('promoFallbackTitle')}>
        {FALLBACK_PROMOS.map((card) => (
          <Link
            key={card.id}
            to={card.link_url}
            className={`campaign-card campaign-card-tone-${card.tone}`}
            role="listitem"
          >
            <span className="campaign-card-fallback" aria-hidden="true" />
            <span className="campaign-card-label">{t(card.titleKey)}</span>
          </Link>
        ))}
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div className={railClass} role="list">
      {items.map((card) => {
        const href = resolveHref(card);
        const hasArt = Boolean(card.image_url);
        const body = (
          <>
            {hasArt ? (
              <img
                src={card.image_url!}
                alt={card.title || card.slug}
                className="campaign-card-art"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <span className="campaign-card-fallback" aria-hidden="true" />
            )}
            {hasArt ? null : <span className="campaign-card-label">{card.title || card.slug}</span>}
          </>
        );

        if (!href) {
          return (
            <div key={card.id || card.slug} className="campaign-card" role="listitem">
              {body}
            </div>
          );
        }

        if (href.startsWith('/play/')) {
          return (
            <button
              key={card.id || card.slug}
              type="button"
              className="campaign-card campaign-card-btn"
              role="listitem"
              onClick={() => {
                rememberCatalogPosition('/');
                navigate(href);
              }}
            >
              {body}
            </button>
          );
        }

        return (
          <Link key={card.id || card.slug} to={href} className="campaign-card" role="listitem">
            {body}
          </Link>
        );
      })}
    </div>
  );
}
